import React, { useContext, useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { AuthContext } from '../login/AuthContext';
import { useNavigate } from 'react-router-dom';
import translations from '../../utils/language/translations.json';
import { useTranslation } from 'react-i18next';
import { hasConsent } from '../login/hasConsent';

const PrivacySettings = () => {
  const { t: i18nT, i18n } = useTranslation();
  const currentLang = i18n.language;
  const t = (key) => (translations?.Privacy?.[key]?.[currentLang]) ?? key;

  const { userId } = useContext(AuthContext);
  const navigate = useNavigate();

  const [profilePrivacy, setProfilePrivacy] = useState(''); // optional “master” toggle
  const [fieldPrivacies, setFieldPrivacies] = useState({});
  const [allowUserToSendOffer, setAllowUserToSendOffer] = useState(true);

  // --- groups: left = profile details, right = cards/visibility ---
  const PROFILE_FIELDS = useMemo(
    () => [
      'PrivacyZipCode',
      'PrivacyCity',
      'PrivacyCountry',
      'PrivacyPhone',
      'PrivacyEbayProfil',
      'PrivacyFacebookProfil',
      'PrivacyComCProfil',
      'PrivacyEpackProfil',
      'PrivacyTwitterProfil',
      'PrivacyInstagramProfil',
    ],
    []
  );

  const CARD_FIELDS = useMemo(
    () => [
      'PrivacyShowPC',
      'PrivacyShowForTradeCard',
      // You can add more card-related keys later here
    ],
    []
  );

  // Redirect if not logged in
  useEffect(() => {
    if (!userId) {
      toast.error(t('Unauthorized! Please log in'));
      if (hasConsent()) {
        localStorage.setItem(
          'intendedPath',
          window.location.pathname + window.location.search
        );
      }
      navigate('/login');
    }
  }, [userId, navigate]);

  // Fetch privacy settings from API
  useEffect(() => {
    const fetchPrivacySettings = async () => {
      try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_API_BASE}/api/get-privacy-settings/${userId}`,
          { withCredentials: true }
        );

        setFieldPrivacies({
          PrivacyZipCode: data.PrivacyZipCode,
          PrivacyCity: data.PrivacyCity,
          PrivacyCountry: data.PrivacyCountry,
          PrivacyEbayProfil: data.PrivacyEbayProfil,
          PrivacyFacebookProfil: data.PrivacyFacebookProfil,
          PrivacyComCProfil: data.PrivacyComCProfil,
          PrivacyEpackProfil: data.PrivacyEpackProfil,
          PrivacyTwitterProfil: data.PrivacyTwitterProfil,
          PrivacyInstagramProfil: data.PrivacyInstagramProfil,
          PrivacyPhone: data.PrivacyPhone,
          PrivacyShowPC: data.PrivacyShowPC,
          PrivacyShowForTradeCard: data.PrivacyShowForTradeCard,
        });

        setAllowUserToSendOffer(!!data.PrivacyAllowUsersToSendOffer);
      } catch (error) {
        toast.error(t('Error fetching privacy settings.'));
        console.error('Error fetching privacy settings:', error);
      }
    };

    if (userId) fetchPrivacySettings();
  }, [userId]);

  // Master profile privacy (optional convenience)
  const handleProfilePrivacyChange = (value) => {
    setProfilePrivacy(value);
    setFieldPrivacies((prev) =>
      Object.keys(prev).reduce((acc, field) => {
        acc[field] = value;
        return acc;
      }, {})
    );
  };

  // Individual field toggle
  const handleFieldPrivacyChange = (fieldName, value) => {
    setFieldPrivacies((prev) => ({ ...prev, [fieldName]: value }));
  };

  // Save
  const handleSavePrivacySettings = async () => {
    try {
      await axios.post(
        `${process.env.REACT_APP_API_BASE}/api/update-privacy-settings`,
        { fieldPrivacies, allowUserToSendOffer },
        { withCredentials: true }
      );
      toast.success(t('Privacy settings updated successfully!'));
    } catch (error) {
      toast.error(t('Error updating privacy settings.'));
      console.error('Error updating privacy settings:', error);
    }
  };

  // Small helper for rendering a grid of fields
  const renderFieldGrid = (fields) => (
    <div className="privacy-grid">
      {fields.map((fieldName) => {
        const privacyValue = fieldPrivacies[fieldName];
        if (privacyValue == null) return null; // not loaded yet
        return (
          <div key={fieldName} className="privacy-field">
            <div className="field-name">{t(fieldName)}</div>
            <div className="privacy-option">
              <button
                className={`privacy-btn ${privacyValue === 'Public' ? 'active' : ''}`}
                aria-pressed={privacyValue === 'Public'}
                onClick={() => handleFieldPrivacyChange(fieldName, 'Public')}
              >
                {t('Public')}
              </button>
              <button
                className={`privacy-btn ${privacyValue === 'Private' ? 'active' : ''}`}
                aria-pressed={privacyValue === 'Private'}
                onClick={() => handleFieldPrivacyChange(fieldName, 'Private')}
              >
                {t('Private')}
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="privacy-settings">
      {/* Top: master choice */}
      <h2 className="privacy-title">{t('Profile Privacy')}</h2>
      <div className="radio-group" role="radiogroup" aria-label={t('Profile Privacy')}>
        <label
          className={`radio-option ${profilePrivacy === 'Public' ? 'active' : ''}`}
          role="radio"
          aria-checked={profilePrivacy === 'Public'}
          tabIndex={0}
        >
          <input
            type="radio"
            name="profilePrivacy"
            value="Public"
            checked={profilePrivacy === 'Public'}
            onChange={() => handleProfilePrivacyChange('Public')}
          />
          {t('Public - Your entire profile is visible to others.')}
        </label>

        <label
          className={`radio-option ${profilePrivacy === 'Private' ? 'active' : ''}`}
          role="radio"
          aria-checked={profilePrivacy === 'Private'}
          tabIndex={0}
        >
          <input
            type="radio"
            name="profilePrivacy"
            value="Private"
            checked={profilePrivacy === 'Private'}
            onChange={() => handleProfilePrivacyChange('Private')}
          />
          {t('Private - Your profile is not visible to others.')}
        </label>
      </div>

      {/* Two-column sections */}
      <div className="privacy-sections">
        <section className="privacy-section">
          <h3 className="privacy-subtitle">{t('Profile details')}</h3>
          {renderFieldGrid(PROFILE_FIELDS)}
        </section>

      <section className="privacy-section">
  <h3 className="privacy-subtitle">{t('Cards & visibility')}</h3>

  <div className="privacy-grid">
    {/* 1) OFFERS FIRST — full-row */}
    <div className="privacy-field full-row">
      <div className="field-name">{t('Allow users to send offers')}</div>
      <div className="privacy-option">
        <button
          className={`privacy-btn ${allowUserToSendOffer ? 'active' : ''}`}
          aria-pressed={allowUserToSendOffer}
          onClick={() => setAllowUserToSendOffer(true)}
        >
          {t('Enabled')}
        </button>
        <button
          className={`privacy-btn ${!allowUserToSendOffer ? 'active' : ''}`}
          aria-pressed={!allowUserToSendOffer}
          onClick={() => setAllowUserToSendOffer(false)}
        >
          {t('Disabled')}
        </button>
      </div>
    </div>

    {/* 2) Then the rest of the card/visibility fields */}
    {CARD_FIELDS.map((fieldName) => {
      const privacyValue = fieldPrivacies[fieldName];
      if (privacyValue == null) return null;
      return (
        <div key={fieldName} className="privacy-field">
          <div className="field-name">{t(fieldName)}</div>
          <div className="privacy-option">
            <button
              className={`privacy-btn ${privacyValue === 'Public' ? 'active' : ''}`}
              aria-pressed={privacyValue === 'Public'}
              onClick={() => handleFieldPrivacyChange(fieldName, 'Public')}
            >
              {t('Public')}
            </button>
            <button
              className={`privacy-btn ${privacyValue === 'Private' ? 'active' : ''}`}
              aria-pressed={privacyValue === 'Private'}
              onClick={() => handleFieldPrivacyChange(fieldName, 'Private')}
            >
              {t('Private')}
            </button>
          </div>
        </div>
      );
    })}
  </div>
</section>

      </div>

      <button className="update-btn" onClick={handleSavePrivacySettings}>
        {t('Update Settings')}
      </button>
    </div>
  );
};

export default PrivacySettings;
